/* eslint-disable react/jsx-indent */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import {Menu} from '@headlessui/react';
import {Link, useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import moment from 'moment';
import * as Actions from '../../../store/actions';
import {edit, eye, resend, trashRed} from '../../../assets/images/images';
import {
  Pagination,
  PrimaryButton,
  SecondaryButton,
  TableLayout,
  PageLimit,
  EmptyState,
  SearchInput,
  TeamesDropdown as Dropdown,
} from '../../../components';
import Loader from '../../../components/Loader';
import SettingsLayout from '../SettingsLayout';
import EditMemberModal from './EditMemberModal';
import InviteMemberModal from './InviteMemberModal';
import ViewMemberModal from './ViewMemberModal';
import {PageContext, TourContext} from '../../../helpers/context';
import {TeamTour} from '../../../components/Tour';
import TransferOwnership from './TransferOwnershipModal';
import {parseUrlParams} from '../../../helpers';
import DeactivateMemberModal from './DeactivateMemberModal';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';

const SettingsTeam = ({
  getTeamMembers,
  getTeamRoles,
  inviteTeamMember,
  removeTeamMember,
  editTeamMemberRole,
  getAllApps,
  assignApp,
  team: {members, roles, loading},
  auth: {userDetails},
}) => {
  const navigate = useNavigate();
  const initialRender = useRef(true);
  const [openFilter, setOpenFilter] = useState(false);
  const [open, setOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState({});
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    filter: '',
    status: '',
  });
  const [transferModal, setTransferModal] = useState(false);

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('teams');
  }, [setPageName]);

  const filterOptions = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        {value: 'active', label: 'Active'},
        {value: 'pending', label: 'Pending'},
        {value: 'expired', label: 'Expired'},
      ],
      multiSelect: false,
    },
  ];

  useEffect(() => {
    const apiParams = parseUrlParams();
    apiParams.limit = query.limit;
    apiParams.page = query.page;
    if (query.filter) {
      apiParams.filter = query.filter;
    }
    if (query.status) {
      apiParams.status = query.status;
    }
    const queryString = new URLSearchParams(apiParams).toString();
    getTeamMembers(queryString);
  }, [getTeamMembers, query.page, query.limit, query.filter, query.status]);

  const handleFilterChange = newFilters => {
    setQuery(prev => ({
      ...prev,
      page: 1,
      status: newFilters.status || '',
    }));
    const searchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.set(key, value);
      }
    });
    if (query.filter) {
      searchParams.set('filter', query.filter);
    }
    navigate(`?${searchParams.toString()}`);
  };

  const handleResetFilter = () => {
    setQuery(prev => ({...prev, page: 1, filter: '', status: ''}));
    navigate('');
    getTeamMembers(new URLSearchParams({}).toString());
  };

  useEffect(() => {
    if (initialRender.current) {
      getTeamRoles();
      getAllApps({limit: '500'});
      initialRender.current = false;
    }
  }, [getTeamRoles, getAllApps]);

  const diffInHours = useCallback(
    (date1, date2) => Math.abs(date1 - date2) / 36e5,
    [],
  );

  const takeAction = member => {
    if (
      member.status !== 'active' &&
      diffInHours(new Date(member.createdAt), new Date()) > 24
    ) {
      inviteTeamMember(
        {
          email: member.email,
          role: member.permissions?.role,
        },
        () => setOpen(false),
      );
    } else {
      setSelectedMember({
        id: member._id,
        email: member.email,
        role: member.permissions?.role,
        user: member.user,
        apps: member.apps,
      });
      setEditModal(!editModal);
    }
  };

  const {
    managePermissions: canManagePermissions,
    manageMembers: canManageMembers,
  } = useMemo(
    () => userDetails?.permissions?.settingsPermissions || {},
    [userDetails],
  );

  const {tourState, updateTourState} = useContext(TourContext);

  const isOwner = userDetails?.permissions?.role === 'Owner';

  const actionMenuItems = useMemo(
    () => [
      {
        icon: eye,
        label: 'View',
        action: () => setViewModal(!viewModal),
      },
      {
        icon: edit,
        label: 'Edit',
        action: () => setEditModal(!editModal),
      },
      {
        icon: resend,
        label: 'Resend Invite',
        action: member => {
          if (
            member.status !== 'active' &&
            diffInHours(new Date(member.createdAt), new Date()) > 24
          ) {
            inviteTeamMember(
              {
                email: member.email,
                role: member.permissions?.role,
              },
              () => setOpen(false),
            );
          }
        },
      },
      {
        icon: trashRed,
        label: 'Delete',
        action: () => setDeactivateModal(!deactivateModal),
      },
    ],
    [deactivateModal, editModal, viewModal, inviteTeamMember, diffInHours],
  );

  const isFiltered =
    !loading &&
    members?.team?.length === 0 &&
    (Object.keys(parseUrlParams()).length > 2 || query.filter || query.status);

  return (
    <SettingsLayout breadCrumbs="Team">
      <div className="flex items-center justify-between min-h-[52px] py-2 flex-wrap gap-3 mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <div className="flex items-center gap-2">
          <SearchInput
            onChange={text => {
              setQuery(prev => ({...prev, filter: text}));
            }}
            defaultValue={query.filter}
          />
          <SecondaryButton
            buttonText="Filter"
            onClick={() => setOpenFilter(prev => !prev)}
          />
        </div>

        <div className="flex items-center gap-4">
          <Link to="/settings/team/owner">
            <SecondaryButton
              disabled={!canManagePermissions}
              style={{cursor: !canManagePermissions && 'not-allowed'}}
              buttonText="Manage Roles & Permissions"
            />
          </Link>
          <PrimaryButton
            buttonText="Invite a Member"
            onClick={() => setOpen(!open)}
            disabled={!canManageMembers}
            style={{cursor: !canManageMembers && 'not-allowed'}}
          />
          {isOwner && (
            <button
              className="px-4 pt-[18px] pb-[14px] bg-white text-brandBlue rounded text-sm font-medium"
              onClick={() => setTransferModal(true)}
            >
              Transfer ownership
            </button>
          )}
        </div>
      </div>

      <div className={`filter-slide-container ${openFilter ? 'open' : ''}`}>
        <FilterV2
          filters={filterOptions}
          onFilterChange={handleFilterChange}
          disableDefaultDateFilter={true}
        />
      </div>

      {tourState?.team_tour && (
        <TeamTour
          updateTourState={updateTourState}
          DATA={members?.team}
          loading={loading}
        />
      )}

      <InviteMemberModal
        open={open}
        setOpen={setOpen}
        roles={roles}
        inviteTeamMember={inviteTeamMember}
        loading={loading}
      />

      <DeactivateMemberModal
        loading={loading}
        selectedMember={selectedMember}
        removeTeamMember={removeTeamMember}
        deactivateModal={deactivateModal}
        setDeactivateModal={setDeactivateModal}
        setOpen={setOpen}
      />
      <EditMemberModal
        open={editModal}
        setOpen={setEditModal}
        roles={roles}
        editTeamMemberRole={editTeamMemberRole}
        loading={loading}
        removeTeamMember={removeTeamMember}
        selectedMember={selectedMember}
        deactivateModal={deactivateModal}
        setDeactivateModal={setDeactivateModal}
        assignApp={assignApp}
      />
      <ViewMemberModal
        takeAction={takeAction}
        viewModal={viewModal}
        setViewModal={setViewModal}
        roles={roles}
        editTeamMemberRole={editTeamMemberRole}
        loading={loading}
        removeTeamMember={removeTeamMember}
        selectedMember={selectedMember}
        deactivateModal={deactivateModal}
        setDeactivateModal={setDeactivateModal}
      />
      <TransferOwnership
        open={transferModal}
        setOpen={setTransferModal}
        roles={roles}
        selectedMember={selectedMember}
      />

      <section className="mt-6">
        {loading ? (
          <Loader height={45} />
        ) : !loading && !isFiltered && members?.team?.length === 0 ? (
          <EmptyState
            body="No data was found."
            buttonText="Invite a member"
            onClick={() => setOpen(!open)}
            disabled={!canManageMembers}
            style={{cursor: !canManageMembers && 'not-allowed'}}
            height={45}
          />
        ) : !loading && isFiltered && members?.team?.length === 0 ? (
          <EmptyState
            body="No results found"
            noBtn
            height={45}
            customBtn={
              <button
                onClick={handleResetFilter}
                className="text-brandBlue p-4 text-sm font-medium"
              >
                Update preferences
              </button>
            }
          />
        ) : (
          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Name of team member</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Email address</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Role</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Date added</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Status</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="text-sm bg-white">
              {members?.team?.map((member, i) => {
                const isExpired =
                  member.status !== 'active' &&
                  diffInHours(new Date(member.createdAt), new Date()) > 24;
                return (
                  <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                    <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                      {member.user?.first_name
                        ? `${member.user.first_name} ${member.user?.last_name}`
                        : '-'}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap capitalize">
                      {member.email}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap capitalize">
                      {member.permissions?.role}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap capitalize">
                      {moment(member.createdAt).format('Do, MMMM YYYY, hh:mmA')}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <div
                        className={`px-2 py-1 text-xs font-medium uppercase rounded-full text-success20 ${
                          member.status !== 'active'
                            ? isExpired
                              ? 'bg-status-500'
                              : 'bg-brandBlue50'
                            : 'bg-success'
                        } w-fit`}
                      >
                        {member.status !== 'active'
                          ? isExpired
                            ? 'expired'
                            : 'pending'
                          : member.status}
                      </div>
                    </td>
                    <td className="text-sm text-center text-grey80 whitespace-nowrap">
                      {member.user?._id !== members.admin && (
                        <div className="flex justify-center">
                          <Dropdown>
                            {actionMenuItems
                              .filter(
                                item =>
                                  !(
                                    item.label === 'Resend Invite' && !isExpired
                                  ),
                              )
                              .map(item => (
                                <Menu.Item
                                  disabled={!canManageMembers}
                                  key={item.label}
                                >
                                  {() => (
                                    <button
                                      onClick={() => {
                                        setSelectedMember({
                                          id: member._id,
                                          email: member.email,
                                          role: member.permissions?.role,
                                          user: member.user,
                                          apps: member.apps,
                                          status: member.status,
                                          createdAt: member.createdAt,
                                        });
                                        item.action(member);
                                      }}
                                      disabled={!canManageMembers}
                                      className="z-40 flex items-center gap-4 w-full font-medium text-gray-500 text-tiny"
                                    >
                                      <img src={item.icon} alt="" />
                                      {item.label}
                                    </button>
                                  )}
                                </Menu.Item>
                              ))}
                          </Dropdown>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableLayout>
        )}

        {!loading && members?.team?.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4  bg-white80 mx-[-32px] px-8 py-[18px]">
            <PageNumber
              currentPage={Number(members?.currentPage)}
              limit={query.limit}
              totalCount={members?.totalRecords}
            />
            <div className="flex items-center gap-5">
              <PageLimit
                onLimitChange={limit => {
                  setQuery(prev => ({...prev, limit, page: 1}));
                }}
                total={members?.totalRecords}
                length={members?.team?.length}
                limit={query.limit}
              />
              <Pagination
                total={members?.totalPages}
                current={Number(members?.currentPage)}
                onPageChange={activePage => {
                  setQuery(prev => ({...prev, page: activePage}));
                  const searchParams = new URLSearchParams(
                    document.location.search,
                  );
                  searchParams.set('page', activePage);
                  navigate(`?${searchParams.toString()}`);
                }}
              />
            </div>
          </div>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-4 sm:hidden">
        <Link to="/settings/team/owner" className="w-full">
          <SecondaryButton
            buttonText="Manage Roles & Permissions"
            className="w-full"
          />
        </Link>
        {canManageMembers && (
          <PrimaryButton
            buttonText="Invite a member"
            onClick={() => setOpen(!open)}
            className="w-full"
          />
        )}
      </div>
    </SettingsLayout>
  );
};

export default connect(state => state, Actions)(SettingsTeam);
